import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAppForm } from "@/hooks/useForm";
import { cerrarFase, imputarFase, iniciarFase, type DatosFabricando, type Imputacion } from "@/ts/api/API";
import { Constantes, REGEX_UBICACION, REGEX_CONTENEDOR, REGEX_PUESTO } from "@/ts/constantes";
import z from "zod";
import { SelectorComponentes } from "./FormComponentes";
import { Input } from "@/components/ui/input";
import { updateCacheOF } from "@/hooks/Api/OrdenesFabricacion";
import { toast } from "react-toastify";

interface FormImputarProps {
	readonly datos: DatosFabricando;
	readonly close: () => void;
}
export function FormImputar(props: FormImputarProps) {
	const { datos } = props;

	const form = useAppForm({
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- Es necesario para "tipar" el form
		defaultValues: {
			cantProducida: props.datos.cantLanzada - props.datos.cantAcabada,
			fechaImputacion: new Date().toISOString().split("T")[0],
			ubicacionFabricado: Constantes.UBICACION_PERDIDOS,
			consumos: [],
		} satisfies iModelImputacion as iModelImputacion,
		validators: {
			onSubmit: zModelImputacion,
		},
		onSubmitMeta: { alsoCerrar: false },
		onSubmit: async ({ value, meta }) => {
			await imputar(meta.alsoCerrar, value);
			form.reset();
		},
	});

	const imputarMutation = useMutationInputar(datos.idOF, datos.idFase);


	const imputar = (alsoCerrar: boolean, imputacion: iModelImputacion) => {
		const finalImputacion: Imputacion = {
			idOF: datos.idOF,
			idFase: datos.idFase,
			fechaImputacion: Math.trunc(new Date(imputacion.fechaImputacion).getTime() / 1000),
			cantProducida: imputacion.cantProducida,
			ubicacion: null,
			contenedor: null,
			consumos: [],
		};

		if (REGEX_UBICACION.test(imputacion.ubicacionFabricado)) finalImputacion.ubicacion = imputacion.ubicacionFabricado;
		if (REGEX_CONTENEDOR.test(imputacion.ubicacionFabricado)) finalImputacion.contenedor = Number(REGEX_CONTENEDOR.exec(imputacion.ubicacionFabricado)![1]);
		if (REGEX_PUESTO.test(imputacion.ubicacionFabricado)) finalImputacion.ubicacion = imputacion.ubicacionFabricado;

		finalImputacion.consumos = imputacion.consumos.map(consumo => {
			let ubicacion: string | null = null;
			let contenedor: number | null = null;
			if (consumo.idUbicacion) {
				if (REGEX_UBICACION.test(consumo.idUbicacion)) ubicacion = consumo.idUbicacion;
				if (REGEX_CONTENEDOR.test(consumo.idUbicacion)) contenedor = Number(consumo.idUbicacion);
				if (REGEX_PUESTO.test(consumo.idUbicacion)) ubicacion = consumo.idUbicacion;
			}

			return {
				...consumo,
				idUbicacion: ubicacion ?? null,
				idContenedor: contenedor ?? null,
			};
		});

		const isValid = ValidarImputacion(finalImputacion);
		if (!isValid) return;

		const alsoAbrir = datos.timestampIni == null;
		return imputarMutation.mutateAsync({ imputacion, alsoAbrir, alsoCerrar })
			.then((success) => {
				if (success) props.close();
			});
	};
	const nextFase = datos.fases
		.sort((itemA, itemB) => itemA.idFase - itemB.idFase)
		.find(fase => fase.idFase > datos.idFase);


	return (
		<>
			<div className="modal-body">
				<div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "16px" }}>
					<form.AppField name="cantProducida">
						{field => (
							<field.NumberField
								label="Cant. Producida"
								placeholder=""
								min={1}
								autoComplete="fabricated-quantity"
							/>
						)}
					</form.AppField>

					<form.AppField name="fechaImputacion">
						{field => (
							<field.DateField
								label="Fecha Imputación"
								placeholder=""
								min={1}
								autoComplete="fabricated-quantity"
							/>
						)}
					</form.AppField>

					{Constantes.existenFases ? <>
						<Input label="Fase" readOnly disabled value={datos.descFase} />
						<Input label="Siguiente Fase" readOnly disabled value={nextFase?.descFase ?? "Fase Final"} />
					</> : null}

					<form.AppField name="ubicacionFabricado">
						{field => (
							<field.TextField
								label="Ubicacion Prod. Terminado"
								placeholder=""
								// pattern="(^[A-Z]{1}\d{2}[A-Z]{1}\d{2}$)|(^P-\d{2}$)|(^\d+$)"
								autoComplete="fabricated-quantity"
								disabled={Constantes.articulosDesubicados}
							/>
						)}
					</form.AppField>

					<form.AppField name="consumos">
						{field => (
							<div style={{ gridColumn: "1 / -1" }}>
								<h3>Componentes</h3>
								<form.Subscribe selector={state => state.values.cantProducida}>
									{cantProducida => (
										<SelectorComponentes
											cantidadFabricada={cantProducida}
											componentesOF={datos.componentes}
											setConsumos={field.setValue}
										/>
									)}
								</form.Subscribe>
							</div>
						)}
					</form.AppField>
				</div>
			</div>
			<div className="modal-footer">
				<button type="button" className="btnSecondary" onClick={props.close}>Cancelar</button>
				<button type="button" className="btnPrimary" onClick={() => void form.handleSubmit({ alsoCerrar: false })}>Imputar</button>
				<button type="button" className="btnSecondary" onClick={() => void form.handleSubmit({ alsoCerrar: true })}>Imputar y cerrar</button>
			</div>
		</>
	);
}


export const zConsumo = z.object({
	idArticulo: z.union([z.string(), z.number()]),
	faseArticulo: z.number(),
	loteArticulo: z.string(),

	idUbicacion: z.string(),

	cantidadUsada: z.number(),
	cantidadDefectuosa: z.number(),
	cantidadDesperdiciada: z.number(),
});
export const zModelImputacion = z.object({
	cantProducida: z.number().min(1),
	fechaImputacion: z.iso.date(),
	ubicacionFabricado: z.string(),
	consumos: z.array(zConsumo),
});
export type iModelImputacion = z.infer<typeof zModelImputacion>;
export type iConsumo = z.infer<typeof zConsumo>;

export type ConsumoOrNot = iConsumo | {
	idArticulo: string | number;
	faseArticulo: number;

	idUbicacion?: null;
	idContenedor?: null;
	loteArticulo?: null;

	cantidadUsada?: null;
	cantidadDefectuosa?: null;
	cantidadDesperdiciada?: null;
};

function sinUbicacion(consumo: Imputacion["consumos"][number]) {
	return consumo.idUbicacion == null && consumo.idContenedor == null;
}

function ValidarImputacion(finalImputacion: Imputacion): boolean {
	if (isNaN(finalImputacion.cantProducida) || finalImputacion.cantProducida < 0) {
		toast("La cantidad producida no es un número válido", { type: "warning" });
		return false;
	}
	if (isNaN(finalImputacion.fechaImputacion)) {
		toast("La fecha introducida no es válida", { type: "warning" });
		return false;
	}
	if (finalImputacion.ubicacion == null && finalImputacion.contenedor == null) {
		toast("La ubicación de salida no es válida", { type: "warning" });
		return false;
	}
	if (finalImputacion.consumos.length == 0) {
		toast("No se puede imputar una fase sin consumos", { type: "warning" });
		return false;
	}

	if (finalImputacion.consumos.some(sinUbicacion)) {
		toast("La ubicación de algún componente no es válida", { type: "warning" });
		return false;
	}
	return true;
}

async function InputarAsync(imputacion: Imputacion, alsoIniciar: boolean, alsoCerrar: boolean) {
	if (alsoIniciar) {
		const result = await iniciarFase({
			idOF: imputacion.idOF,
			idFase: imputacion.idFase,
			fechaInicio: imputacion.fechaImputacion,
		});

		if (result.status == "error") {
			toast(result.message, { type: "error" });
			return false;
		}
	}

	const result = await imputarFase(imputacion);
	if (result.status == "error") {
		toast(result.message, { type: "error" });
		return false;
	}

	if (alsoCerrar) {
		const resultCerrar = await cerrarFase({
			idOF: imputacion.idOF,
			idFase: imputacion.idFase,
			fechaCierre: imputacion.fechaImputacion,
		});

		if (resultCerrar.status == "error") {
			toast(resultCerrar.message, { type: "error" });
			return false;
		}
	}
	return true;
}

function useMutationInputar(idOF: number, idFase: number) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ imputacion, alsoAbrir, alsoCerrar }: { imputacion: iModelImputacion, alsoAbrir: boolean, alsoCerrar: boolean; }) => {
			const finalImputacion: Imputacion = {
				idOF: idOF,
				idFase: idFase,
				fechaImputacion: Math.trunc(new Date(imputacion.fechaImputacion).getTime() / 1000),
				cantProducida: imputacion.cantProducida,
				ubicacion: null,
				contenedor: null,
				consumos: [],
			};

			if (REGEX_UBICACION.test(imputacion.ubicacionFabricado)) finalImputacion.ubicacion = imputacion.ubicacionFabricado;
			if (REGEX_CONTENEDOR.test(imputacion.ubicacionFabricado)) finalImputacion.contenedor = Number(imputacion.ubicacionFabricado);
			if (REGEX_PUESTO.test(imputacion.ubicacionFabricado)) finalImputacion.ubicacion = imputacion.ubicacionFabricado;

			finalImputacion.consumos = imputacion.consumos.map(consumo => {
				let ubicacion: string | null = null;
				let contenedor: number | null = null;
				if (consumo.idUbicacion) {
					if (REGEX_UBICACION.test(consumo.idUbicacion)) ubicacion = consumo.idUbicacion;
					else if (REGEX_CONTENEDOR.test(consumo.idUbicacion)) contenedor = Number(consumo.idUbicacion);
					else if (REGEX_PUESTO.test(consumo.idUbicacion)) ubicacion = consumo.idUbicacion;
				}

				return {
					...consumo,
					idUbicacion: ubicacion ?? null,
					idContenedor: contenedor ?? null,
				};
			});

			if (isNaN(finalImputacion.cantProducida) || finalImputacion.cantProducida < 0) {
				return Promise.reject(new Error("La cantidad producida no es un número válido"));
			}

			return InputarAsync(finalImputacion, alsoAbrir, alsoCerrar);
		},
		onSuccess: (resultado) => {
			if (!resultado) return false;

			void updateCacheOF(idOF, queryClient);
			void queryClient.invalidateQueries({ queryKey: ["imputacionesFase", idOF, idFase] });
			void queryClient.invalidateQueries({ queryKey: ["getInfoFabricando", { idOF, idFase }] });

			return true;
		},
		onError: (error) => {
			console.error("Error imputando:", error);
			toast(error instanceof Error ? error.message : "Error al imputar", { type: "error" });
		},
	});
}